import React, { useEffect, useRef, useState } from "react";
import Header from "@/components/layout/headers/Header/Header";
import Footer from "@/components/layout/Footer/Footer";
import { useConfig } from "@/context/ConfigContext";
import NotificationPanel from "@/components/layout/NotificationPanel";
import { useSelector, useDispatch } from "react-redux";
import AppDrawer from "@/components/Drawers/AppDrawer/AppDrawer";
import SupportDetailCard from "@/components/SupportDetailCard";
import { useRouter } from "next/router";
import Head from "next/head";
import useScrollToElement from "@/hooks/scrollToTop";
import { RootState } from "@/redux/types/RootState";
import useNetworkStatus from "@/hooks/useNetworkStatus";
import { useNotify } from "@/hooks/useNotify";
import RoleChangeWatcher from "@/hooks/RoleChangeWatcher";
import DevIndicator from "../DevIndicator/DevIndicator";

interface MainLayoutProps {
  children: React.ReactNode;
}

const noAuthRoutes = new Set([
  "/signup",
  "/reset-password",
  "/confirm-password",
  "/verify-password",
  "/sessionExpired",
]);

const hideHeaderFooterRoutes = new Set([
  "/signin",
  "/login-confirm",
  "/reset-password",
  "/confirm-password",
  "/verify-password",
  "/sessionExpired",
]);

const footerExcludedRoutes = new Set([
  "/signup",
  "/privacy",
  "/terms-and-conditions",
  "/404",
]);

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { config } = useConfig();
  const { isOnline } = useNetworkStatus();
  const notify = useNotify();

  const clientName = "GTE Connects";
  const clientFavicon = config?.theme.images.favicon;
  const notifications = useSelector((state: RootState) => state.notification);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const router = useRouter();
  const scrollToRef = useScrollToElement();

  const isAuthRequired = !noAuthRoutes.has(router.pathname);
  const isHeaderFooterHidden = hideHeaderFooterRoutes.has(router.pathname);
  const isFooterHidden = footerExcludedRoutes.has(router.pathname);
  const showDevIndicator = process.env.NEXT_PUBLIC_DEV_INDICATOR === "true";
  const mainPaddingTopClass = isHeaderFooterHidden
    ? (showDevIndicator ? "pt-10" : "pt-0")
    : (showDevIndicator ? "pt-[119px]" : "pt-[79px]");
  const dispatch = useDispatch();
  const previousIsOnline = useRef<boolean | null>(null);
  useEffect(() => {
    if (previousIsOnline.current === null) {
      previousIsOnline.current = isOnline;
      return;
    }
    if (isOnline !== previousIsOnline.current) {
      if (isOnline) {
        notify(
          "success",
          "Connection Restored",
          "Your internet connection has been restored.",
        );
      } else {
        notify(
          "error",
          "Connection Error",
          "Connection lost. Please check your internet connection.",
        );
      }
      previousIsOnline.current = isOnline;
    }
  }, [isOnline, dispatch]);

  return (
    <>
      <Head>
        <title>{clientName}</title>
        {clientFavicon && <link rel="icon" href={clientFavicon} />}
      </Head>
      {showDevIndicator && <DevIndicator />}
      <RoleChangeWatcher />
      <div
        className={`relative ${isDrawerOpen ? "overflow-hidden" : ""}`}
        style={{
          overflowX: "hidden",
        }}
        data-testid="MainLayout"
      >
        <AppDrawer
          show={isDrawerOpen}
          onClose={() => setIsDrawerOpen(!isDrawerOpen)}
          title="How can we help you?"
        >
          <SupportDetailCard />
        </AppDrawer>
        <div className="flex flex-col h-screen  ">
          {!isHeaderFooterHidden && (
            <Header
              logoUrl={config?.theme.images.logo}
              auth={isAuthRequired}
              onSupportClicked={() => setIsDrawerOpen(true)}
            />
          )}
          <main ref={scrollToRef} className={`  ${mainPaddingTopClass}  `}>
            <NotificationPanel notifications={notifications} />
            {children}
          </main>
          {!isHeaderFooterHidden && !isFooterHidden && <Footer />}
        </div>
      </div>
    </>
  );
};

export default MainLayout;
